import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { toast } from "react-toastify";
import LeaveProcessService from "../services/LeaveProcessService";
import { useLoading } from "../Utils/LoadingContext";
import "./AttendanceRegularization.css";

export default function AttendanceRegularization() {

    const { setLoading } = useLoading();

    const [activeStartDate, setActiveStartDate] = useState(new Date());
    const [monthSummary, setMonthSummary] = useState([]);

    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedDayData, setSelectedDayData] = useState(null);

    const [regularizationType, setRegularizationType] = useState("");

    const [startHour, setStartHour] = useState("");
    const [startMinute, setStartMinute] = useState("");

    const [endHour, setEndHour] = useState("");
    const [endMinute, setEndMinute] = useState("");

    const [comment, setComment] = useState("");

    const employeeCode = localStorage.getItem("empcode");
    const employeeName = localStorage.getItem("username");
    const today = new Date();

    // ================= LOAD MONTH DATA =================

    useEffect(() => {
        fetchMonthSummary(activeStartDate);
    }, [activeStartDate]);

    const getLeaveMMYY = (date) => {
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${month}${year}`;
    };

    const parseDate = (dateString) => {
        const parts = dateString.split(" ")[0].split("/");
        return new Date(parts[2], parts[0] - 1, parts[1]);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString("en-GB");
    };

    // ================= API =================

    const fetchMonthSummary = async (date) => {

        try {

            setLoading(true);

            const formData = new FormData();
            formData.append("EmployeeCode", employeeCode);
            formData.append("Type", "GetMonthSummary");
            formData.append("LeaveMMYY", getLeaveMMYY(date));

            const response = await LeaveProcessService.getEmployeeCalender(formData);

            if (response.data.statusCode === 200) {
                setMonthSummary(response.data.result || []);
            }

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // ================= STATUS COLORS =================

    const getStatusColor = (date) => {

        const item = monthSummary.find(ms =>
            ms.punchDate &&
            parseDate(ms.punchDate).toDateString() === date.toDateString()
        );

        if (!item) return "";

        switch (item.status) {

            case "PP":
                return "status-pp";
            case "OD":
                return "status-pp";
            case "AR":
                return "status-pp";

            case "AP":
                return "status-ap";

            case "PA":
                return "status-pa";

            case "AA":
                return "status-aa";

            case "WO":
                return "status-wo";
            case "CL":
                return "leave-day";
            case "PL":
                return "leave-day";
            case "ACL":
                return "leave-day";
            case "CLA":
                return "leave-day";

            case "HO":
                return "status-hl";

            default:
                return "status-default";
        }
    };

    // ================= CALENDAR CLICK =================

    // UI format → dd-mm-yyyy
    const formatDateUI = (date) => {

        if (!date) return "";

        const d = new Date(date);

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        return `${day}-${month}-${year}`;
    };


    // API format → yyyy-mm-dd
    const formatDateAPI = (date) => {

        if (!date) return "";

        const d = new Date(date);

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        return `${year}-${month}-${day}`;
    };

    const handleDateClick = (date) => {

        const item = monthSummary.find(ms =>
            ms.punchDate &&
            parseDate(ms.punchDate).toDateString() === date.toDateString()
        );

        setSelectedDate(date);
        setSelectedDayData(item || null);

        // auto select type based on missing punch
        if (item) {
            if (item.firstPunchIn === "-") {
                setRegularizationType("FORGOT_IN");
            } else if (item.lastPunchOut === "-") {
                setRegularizationType("FORGOT_OUT");
            } else {
                setRegularizationType("");
            }
        }

        setStartHour("");
        setStartMinute("");
        setEndHour("");
        setEndMinute("");
        setComment("");
    };

    // ================= INPUT HANDLING =================

    const handleHourChange = (value, setFunc) => {
        if (!/^\d*$/.test(value)) return;
        if (value.length > 2) return;
        setFunc(value);
    };

    const handleMinuteChange = (value, setFunc) => {
        if (!/^\d*$/.test(value)) return;
        if (value.length > 2) return;
        setFunc(value);
    };

    const formatHour = (value, setFunc) => {
        if (value === "") return;
        let num = parseInt(value);
        if (num > 23) num = 23;
        setFunc(num.toString().padStart(2, "0"));
    };

    const formatMinute = (value, setFunc) => {
        if (value === "") return;
        let num = parseInt(value);
        if (num > 59) num = 59;
        setFunc(num.toString().padStart(2, "0"));
    };

    // ================= RADIO =================

    const handleRadioChange = (type) => {

        setRegularizationType(type);

        if (type === "FORGOT_IN") {
            setEndHour("");
            setEndMinute("");
        }

        if (type === "FORGOT_OUT") {
            setStartHour("");
            setStartMinute("");
        }
    };

    // ================= SHIFT DATA =================

    const shiftTime = selectedDayData?.shiftName || "-";
    const checkInTime = selectedDayData?.firstPunchIn || "-";
    const checkOutTime = selectedDayData?.lastPunchOut || "-";

    // ================= BUTTON ENABLE =================

    const isEmptyPunch = (val) => {
        return !val || val === "-" || val === "--" || val === "00:00";
    };

    const isFormValid = () => {

        if (!selectedDate) return false;

        // FIXED CONDITION
        // if (isEmptyPunch(checkInTime) && isEmptyPunch(checkOutTime)) {
        //     return false;
        // }

        if (!regularizationType) return false;

        if (regularizationType === "FORGOT_IN") {
            if (!startHour || !startMinute) return false;
        }

        if (regularizationType === "FORGOT_OUT") {
            if (!endHour || !endMinute) return false;
        }

        if (!comment.trim()) return false;

        return true;
    };

    // ================= SUBMIT =================
    const getFormattedTime = () => {

        if (regularizationType === "FORGOT_IN") {
            return `${startHour.padStart(2, "0")}:${startMinute.padStart(2, "0")}`;
        }

        if (regularizationType === "FORGOT_OUT") {
            return `${endHour.padStart(2, "0")}:${endMinute.padStart(2, "0")}`;
        }

        return "";
    };
    const handleSubmit = async () => {

        if (!isFormValid()) {
            alert("Please fill all required fields");
            return;
        }

        try {

            setLoading(true);

            const formData = new FormData();
            formData.append("EmployeeCode", employeeCode);
            formData.append("FromDate", formatDateAPI(selectedDate));
            formData.append("ToDate", formatDateAPI(selectedDate));
            formData.append("NoOfDays", 1);
            formData.append("LeaveUnit", "FULLDAY");
            // 🔥 CONDITIONAL HalfDayType
            if (regularizationType === "FORGOT_IN") {
                formData.append("HalfDayType", "FORGET_IN");
            }

            if (regularizationType === "FORGOT_OUT") {
                formData.append("HalfDayType", "FORGET_OUT");
            }
            formData.append("LeaveTypeID", 6);
            formData.append("StartTime", getFormattedTime()); // ✅ MAIN FIX
            formData.append("Reason", comment);
            formData.append("Comment", comment);
            formData.append("Type", "APPLY");
            formData.append("EmployeeName", employeeName);

            const response = await LeaveProcessService.setEmployeeLeave(formData);

            if (response.success && response.data.statusCode === 200) {

                alert("Attendance Regularization Submitted");

                setRegularizationType("");
                setStartHour("");
                setStartMinute("");
                setEndHour("");
                setEndMinute("");
                setComment("");
            } else {
                const message = response.errorMessage || 'Submission failed. Please try again.';
                alert(message);
            }

        } catch (error) {
            const message = error?.message || 'Unexpected error occurred.';
            alert(message);
            console.error("Unhandled error:", message);
        } finally {
            setLoading(false);
        }
    };

    // ================= UI =================

    return (

        <div className="container py-3">

            <div className="row">

                {/* CALENDAR */}

                <div className="col-md-4">

                    <Calendar
                        onClickDay={handleDateClick}
                        activeStartDate={activeStartDate}
                        maxDate={today}
                        prev2Label="<<"
                        next2Label=">>"
                        onActiveStartDateChange={({ activeStartDate }) => {
                            setActiveStartDate(activeStartDate);
                        }}
                        tileClassName={({ date, view }) => {
                            if (view === "month") {
                                if (date > today) return "future-date";
                                return getStatusColor(date);
                            }
                        }}
                    />
                    {/* Legend */} <div className="mt-3 text-white" > <span className="small-text"> <span className="small-text" style={{ background: "#28a745", padding: "5px 5px", marginRight: 0 }}>PP - Present</span> </span> <span > <span className="small-text" style={{ background: "#dc3545", padding: "5px 5px", marginRight: 5 }}>AA - Absent</span> </span> <span> <span className="small-text" style={{ background: "#ff9800", padding: "5px 5px", marginRight: 5 }}>AP/PA - Half Day</span> </span> <span> <span className="small-text" style={{ background: "#a7bdc7", padding: "5px 5px", marginRight: 5 }}>WO</span> </span><span> <span className="small-text" style={{ background: "purple", padding: "5px 5px", marginRight: 5 }}>HO</span> </span><span> <span className="small-text" style={{ background: "blue", padding: "5px 5px", marginRight: 5 }}>L</span> </span> </div>
          
                </div>

                {/* RIGHT SIDE FORM */}

                <div className="col-md-8">

                    {!selectedDate ? (

                        <div className="day-details">
                            <h6>Please select a date from calendar for Attendance Regularization</h6>
                        </div>

                    ) : (

                        <div className="ar-modal">

                            <div className="ar-header">
                                <span>Request for {formatDateUI(selectedDate)}  Attendance Regularization</span>
                            </div>

                            <div className="ar-shift-row">
                                <span>Shift : {shiftTime}</span>
                                <span>Check in : {checkInTime}</span>
                                <span>Check out : {checkOutTime}</span>
                            </div>

                            <div className="ar-form">

                                <div className="radio-row">

                                    <label>
                                        <input
                                            type="radio"
                                            checked={regularizationType === "FORGOT_IN"}
                                            onChange={() => handleRadioChange("FORGOT_IN")}
                                        />
                                        Forgot In
                                    </label>

                                    <label>
                                        <input
                                            type="radio"
                                            checked={regularizationType === "FORGOT_OUT"}
                                            onChange={() => handleRadioChange("FORGOT_OUT")}
                                        />
                                        Forgot Out
                                    </label>

                                </div>

                                <div className="form-row">

                                    <div className="field">
                                        <label>From Date</label>
                                        <input value={formatDate(selectedDate)} readOnly />
                                    </div>

                                    <div className="field">
                                        <label>To Date</label>
                                        <input value={formatDate(selectedDate)} readOnly />
                                    </div>

                                </div>

                                <div className="form-row">

                                    <div className="field">
                                        <label>Start Hour</label>
                                        <input
                                            type="text"
                                            maxLength="2"
                                            disabled={regularizationType !== "FORGOT_IN"}
                                            value={startHour}
                                            onChange={(e) => handleHourChange(e.target.value, setStartHour)}
                                            onBlur={() => formatHour(startHour, setStartHour)}
                                        />
                                    </div>

                                    <div className="field">
                                        <label>Start Minute</label>
                                        <input
                                            type="text"
                                            maxLength="2"
                                            disabled={regularizationType !== "FORGOT_IN"}
                                            value={startMinute}
                                            onChange={(e) => handleMinuteChange(e.target.value, setStartMinute)}
                                            onBlur={() => formatMinute(startMinute, setStartMinute)}
                                        />
                                    </div>

                                    <div className="field">
                                        <label>End Hour</label>
                                        <input
                                            type="text"
                                            maxLength="2"
                                            disabled={regularizationType !== "FORGOT_OUT"}
                                            value={endHour}
                                            onChange={(e) => handleHourChange(e.target.value, setEndHour)}
                                            onBlur={() => formatHour(endHour, setEndHour)}
                                        />
                                    </div>

                                    <div className="field">
                                        <label>End Minute</label>
                                        <input
                                            type="text"
                                            maxLength="2"
                                            disabled={regularizationType !== "FORGOT_OUT"}
                                            value={endMinute}
                                            onChange={(e) => handleMinuteChange(e.target.value, setEndMinute)}
                                            onBlur={() => formatMinute(endMinute, setEndMinute)}
                                        />
                                    </div>

                                </div>

                                <div className="field-full">
                                    <label>Comment</label>
                                    <textarea
                                        rows="3"
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                    />
                                </div>

                                <div className="submit-row">
                                    <button
                                        className="submit-btn"
                                        // disabled={!isFormValid()}
                                        onClick={handleSubmit}
                                    >
                                        SUBMIT
                                    </button>
                                </div>

                            </div>

                        </div>

                    )}

                </div>

            </div>

        </div>
    );
}